/**
 * Guided-tour flags + a tiny running-state store (same pattern as
 * `features/marketplace/stores/cartUi`).
 *
 * The tour is armed (`pending`) when the patient finishes the last
 * onboarding step (consent accept — see `useAcceptConsents`) and plays
 * the next time they land on /app Home. `done` is sticky per patient so
 * the forced walkthrough only ever plays once per device. `?tour=1` on
 * any /app route replays it regardless of flags (QA lever).
 */

import { useSyncExternalStore } from 'react';

const PENDING_PREFIX = 'app_tour_pending:';
const DONE_PREFIX = 'app_tour_done:';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode) — tour just won't arm */
  }
}

export function markTourPending(patientId: string) {
  if (read(DONE_PREFIX + patientId) === '1') return;
  write(PENDING_PREFIX + patientId, '1');
  bumpFlagsVersion();
}

export function markTourDone(patientId: string) {
  write(DONE_PREFIX + patientId, '1');
  write(PENDING_PREFIX + patientId, null);
  bumpFlagsVersion();
}

export function shouldAutoStartTour(patientId: string): boolean {
  return (
    read(PENDING_PREFIX + patientId) === '1' &&
    read(DONE_PREFIX + patientId) !== '1'
  );
}

let running = false;
let flagsVersion = 0;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function bumpFlagsVersion() {
  flagsVersion += 1;
  emit();
}

export const tourUi = {
  start() {
    if (running) return;
    running = true;
    emit();
  },
  stop() {
    if (!running) return;
    running = false;
    emit();
  },
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTourRunning(): boolean {
  return useSyncExternalStore(subscribe, () => running);
}

/**
 * Re-renders subscribers whenever a pending/done flag is written — lets
 * AppLayout's upsell-hold re-read `shouldAutoStartTour` the moment the
 * tour engine decides a control-cohort patient will never see the tour.
 */
export function useTourFlagsVersion(): number {
  return useSyncExternalStore(subscribe, () => flagsVersion);
}
